/* ==========================================================================================
   Commercial Equipment Online 2026 · ce-cart-estimate.js · cart extras (snippets/ce-cart-extras). Product builder,
   09-10-2026. Deferred, no dependencies, document-level listeners (Dawn re-draws the drawer after every change).

   1. "Estimate delivery": the live cart's postcode estimator (PARITY d 3), ported to Shopify's own cart shipping
      rates for THIS cart: POST /cart/prepare_shipping_rates.json, then GET /cart/async_shipping_rates.json until the
      carrier (Shipping Rules AI) answers. The state is worked out from the postcode with the live A680 bands, so
      Shopify never answers 422 for a postcode / state mismatch. Asked ONLY when the shopper presses Estimate.
      A $0.00 rate is never printed as free unless its own name says free (the live A344 rule): it reads
      "Calculated after ordering". Cart prices are always inc GST, so these are too.
   2. "You may also need": complementary recommendations for the first item (rows from sections/ce-product-addons),
      leaving out what is already in the cart. Nothing shows when the store has none set (CE today).
   ========================================================================================== */
(() => {
  'use strict';
  if (window.CECartEstimate) return;
  const W = window, D = document;
  const money = (c) => (W.BO && W.BO.money ? W.BO.money(c) : '$' + ((Number(c) || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  const esc = (t) => String(t == null ? '' : t).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  // [lo, hi, state] - the live rb-cart-postcode.js (A680) table
  const BANDS = [[200, 299, 'Australian Capital Territory'], [800, 999, 'Northern Territory'], [1000, 2599, 'New South Wales'], [2600, 2618, 'Australian Capital Territory'], [2619, 2619, 'New South Wales'], [2620, 2899, 'New South Wales'], [2900, 2920, 'Australian Capital Territory'], [2921, 2999, 'New South Wales'], [3000, 3999, 'Victoria'], [4000, 4999, 'Queensland'], [5000, 5999, 'South Australia'], [6000, 6999, 'Western Australia'], [7000, 7999, 'Tasmania'], [8000, 8999, 'Victoria'], [9000, 9999, 'Queensland']];
  const stateFor = (zip) => {
    const d = String(zip || '').replace(/\D/g, '');
    if (d.length !== 3 && d.length !== 4) return null;
    const pc = parseInt(d, 10);
    for (const b of BANDS) if (pc >= b[0] && pc <= b[1]) return b[2];
    return null;
  };
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  /* 1. estimate */
  const estimate = async (box, pc) => {
    const out = box.querySelector('[data-cce-out]');
    const say = (html) => { if (out) out.innerHTML = html; };
    const prov = stateFor(pc);
    if (!/^\d{4}$/.test(pc) || !prov) { say('<p class="cce__msg">Please enter a 4-digit Australian postcode.</p>'); return; }
    say('<p class="cce__msg">Checking ' + esc(pc) + ' ...</p>');
    const qs = 'shipping_address[zip]=' + encodeURIComponent(pc) + '&shipping_address[country]=Australia&shipping_address[province]=' + encodeURIComponent(prov);
    try {
      const p = await fetch('/cart/prepare_shipping_rates.json?' + qs, { method: 'POST', credentials: 'same-origin' });
      if (p.status === 422) {
        const j = await p.json().catch(() => ({}));
        const words = Object.keys(j).map((k) => k + ' ' + [].concat(j[k]).join(', ')).join('; ');
        say('<p class="cce__msg">That postcode could not be priced' + (words ? ': ' + esc(words) : '') + '.</p>');
        return;
      }
      let rates = null;
      for (let i = 0; i < 15 && !rates; i++) {
        const a = await fetch('/cart/async_shipping_rates.json?' + qs, { credentials: 'same-origin' });
        if (a.status === 200) {
          const txt = await a.text();
          if (txt && txt !== 'null') { try { rates = JSON.parse(txt).shipping_rates; } catch (e) { rates = []; } }
        }
        if (!rates) await wait(900);
      }
      if (!rates) { say('<p class="cce__msg">That took too long. Please try again, or ask for a quote.</p>'); return; }
      if (!rates.length) { say('<p class="cce__msg">No delivery option came back for ' + esc(pc) + '. Use Get a quote or contact us and we will price it.</p>'); return; }
      try { localStorage.setItem('ce-postcode', pc); } catch (e) { /* fine */ }
      // (3) the cart quote carries the delivery this estimate showed (the hub asks Shopify again for the real figure)
      try { sessionStorage.setItem('ce-cart-est', JSON.stringify({ pc, rates: rates.map((r) => ({ name: r.presentment_name || r.name, price: r.price })) })); } catch (e) { /* fine */ }
      const rows = rates.map((r) => {
        const cents = Math.round(parseFloat(r.price || '0') * 100);
        const free = cents === 0 && /\bfree\b/i.test(r.name || '');
        const price = cents > 0 ? money(cents) : (free ? 'FREE' : 'Calculated after ordering');
        return '<li class="cce__rate"><span class="cce__name">' + esc(r.presentment_name || r.name) + (r.description ? '<span class="cce__desc">' + esc(r.description) + '</span>' : '') + '</span><span class="cce__price' + (cents > 0 ? '' : ' cce__price--soft') + '">' + esc(price) + '</span></li>';
      }).join('');
      say('<p class="cce__to">To ' + esc(pc) + ', ' + esc(prov) + ' <span class="cce__inc">(inc GST)</span></p><ul class="cce__rates" role="list">' + rows + '</ul>');
    } catch (e) {
      say('<p class="cce__msg">Sorry, the estimate did not come through. Please try again.</p>');
    }
  };
  D.addEventListener('submit', (e) => {
    const f = e.target.closest && e.target.closest('[data-cce-form]');
    if (!f) return;
    e.preventDefault();
    const box = f.closest('[data-cce]');
    const input = f.querySelector('[data-cce-pc]');
    estimate(box, String(input && input.value || '').replace(/\D/g, '').slice(0, 4));
  });
  D.addEventListener('toggle', (e) => {
    const box = e.target;
    if (!box || !box.matches || !box.matches('[data-cce]') || !box.open) return;
    const input = box.querySelector('[data-cce-pc]');
    try { const last = localStorage.getItem('ce-postcode'); if (last && input && !input.value) input.value = last; } catch (er) { /* fine */ }
  }, true);

  /* 2. You may also need */
  const loadAddons = (scope) => {
    (scope || D).querySelectorAll('[data-ccx-addons][data-url]').forEach((box) => {
      if (box.ccxBound) return;
      box.ccxBound = true;
      fetch(box.getAttribute('data-url'), { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const list = doc.querySelector('.cpa');
          if (!list) return;
          const inCart = box.getAttribute('data-in-cart') || ',';
          list.querySelectorAll('[data-cpa-product]').forEach((row) => { if (inCart.indexOf(',' + row.getAttribute('data-cpa-product') + ',') > -1) row.remove(); });
          if (!list.querySelector('.cpa__row')) return;
          const slot = box.querySelector('[data-ccx-addons-slot]');
          if (!slot) return;
          slot.innerHTML = '';
          slot.appendChild(list);
          box.hidden = false;
        })
        .catch(() => { /* nothing to suggest */ });
    });
  };
  const boot = () => loadAddons(D);
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot); else boot();
  // the drawer is re-drawn after every cart change: look again
  if (W.MutationObserver) {
    let t = 0;
    new MutationObserver(() => { clearTimeout(t); t = setTimeout(boot, 120); }).observe(D.body || D.documentElement, { childList: true, subtree: true });
  }
  /* 3. Tommy 15-10-2026: the cart's "Get a quote" is the Quotes AI hook (data-qai-add-cart, snippets/ce-cart-extras
     part 'quote'): the app puts every cart line on the quote and opens its own request form. All this file adds is
     the postcode the shopper last priced delivery to (1. above), handed over as data-qai-postcode so the form starts
     with it and asks the checkout which delivery methods it offers. CAPTURE phase, so it is on the button before the
     app's own click handler reads it. A postcode that is not four digits is not handed over.
     (Replaced the A831 short form + printout that posted to the hub's /site/quote-request, 12-10 to 15-10.) */
  D.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-qai-add-cart]');
    if (!btn) return;
    let pc = '';
    try { pc = String(localStorage.getItem('ce-postcode') || '').replace(/\D/g, ''); } catch (er) { /* fine */ }
    if (pc.length === 4) btn.setAttribute('data-qai-postcode', pc);
    else btn.removeAttribute('data-qai-postcode');
  }, true);

  W.CECartEstimate = { estimate, stateFor };
})();
